/**
 * Centralized Service Configuration
 *
 * Handles URL management for all backend services.
 * - Development: Uses full URLs from environment variables
 * - Production: Uses relative paths (same-origin, ingress routing)
 *
 * Only Keycloak requires a separate domain for security reasons.
 */

// Type for Vite's import.meta.env
type ViteImportMeta = { env?: Record<string, string | boolean | undefined> };

// Helper to get env var (supports both Next.js and Vite conventions)
const getEnv = (key: string): string | undefined => {
  // Try Vite convention first (import.meta.env)
  const meta = import.meta as ViteImportMeta;
  if (meta?.env) {
    const viteKey = key.replace('NEXT_PUBLIC_', 'VITE_');
    const viteVal = meta.env[viteKey];
    if (viteVal && typeof viteVal === 'string') return viteVal;
    const keyVal = meta.env[key];
    if (keyVal && typeof keyVal === 'string') return keyVal;
  }
  // Fall back to process.env (Next.js)
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  return undefined;
};

// Determine environment
// isDev is true in development mode OR when explicitly running locally
const meta = import.meta as ViteImportMeta;
const isDev = (
  (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') ||
  (meta?.env?.DEV === true) ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost')
);

// Extra safety: Explicit flag to enable dev proxies (must be 'true' to enable)
// This prevents accidental exposure if NODE_ENV is misconfigured
export const devProxiesEnabled = isDev && (getEnv('NEXT_PUBLIC_KEYCLOAK_DISABLE') === 'true' || getEnv('VITE_KEYCLOAK_DISABLE') === 'true');

/**
 * Get the base path for the application (for Next.js basePath support)
 * This allows the app to be deployed under a path prefix like /flows-alpha
 *
 * Priority order:
 * 1. Next.js __NEXT_DATA__.basePath (runtime, browser-only)
 * 2. NEXT_PUBLIC_BASE_PATH (direct reference for bundler inlining)
 * 3. Vite VITE_BASE_PATH (import.meta.env)
 * 4. Environment variable via getEnv fallback
 */
const getBasePath = (): string => {
  let basePath = '';

  // In browser, check Next.js __NEXT_DATA__ first (most reliable for Next.js apps)
  if (typeof window !== 'undefined') {
    const nextData = (window as { __NEXT_DATA__?: { basePath?: string } }).__NEXT_DATA__;
    if (nextData?.basePath) {
      basePath = nextData.basePath;
    }
  }

  // Direct reference for bundler inlining (Next.js replaces this at build time)
  if (!basePath && typeof process !== 'undefined' && process.env) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - Direct access for bundler inlining
    basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';
  }

  // Fallback to Vite or dynamic env access
  if (!basePath) {
    basePath = getEnv('NEXT_PUBLIC_BASE_PATH') || getEnv('VITE_BASE_PATH') || '';
  }

  // Normalize: ensure no trailing slash, but keep leading slash if present
  return basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
};

/**
 * Helper to construct API URL with basePath support
 * @param overrideKey - Environment variable key for override
 * @param defaultPath - Default path without basePath (e.g., '/api/agent')
 */
const getApiUrl = (overrideKey: string, defaultPath: string): string => {
  // First check for explicit override
  const override = getEnv(overrideKey);
  if (override) {
    return override;
  }

  // For Vite, also check the VITE_ variant
  const viteKey = overrideKey.replace('NEXT_PUBLIC_', 'VITE_');
  const viteOverride = getEnv(viteKey);
  if (viteOverride) {
    return viteOverride;
  }

  // Use basePath + default path
  const basePath = getBasePath();
  return `${basePath}${defaultPath}`;
};

/**
 * Service URLs Configuration
 *
 * IMPORTANT: The agent service (flows-alpha-agent) is CLUSTER-INTERNAL only.
 * It has NO public ingress. All requests MUST go through the Next.js API proxy
 * which handles:
 *   1. Token injection (from NextAuth session)
 *   2. CSRF protection
 *   3. Routing to cluster-internal service
 *
 * The /api/agent proxy is used in BOTH development and production.
 *
 * All URLs automatically prepend NEXT_PUBLIC_BASE_PATH (or VITE_BASE_PATH) when set,
 * allowing deployment under a path prefix (e.g., /flows-alpha).
 *
 * NOTE: Using getters for lazy evaluation - this ensures NEXT_PUBLIC_BASE_PATH
 * is read at access time, not at module initialization time. This is critical
 * for Next.js apps where env vars may not be available during initial bundling.
 */
export const Services = {
  /**
   * Agent Executor service - ALWAYS uses proxy
   * The proxy route /api/agent/* forwards to cluster-internal flows-alpha-agent service
   * Override via NEXT_PUBLIC_AGENT_EXECUTOR_URL or VITE_AGENT_EXECUTOR_URL for local dev
   */
  get agentExecutor() { return getApiUrl('NEXT_PUBLIC_AGENT_EXECUTOR_URL', '/api/agent'); },

  /** Workflow Manager service */
  get workflowManager() { return getApiUrl('NEXT_PUBLIC_WORKFLOW_MANAGER_URL', '/api/workflow-manager'); },

  /** EAMS - Eureka Access Management Service (RBAC) */
  get accessManagement() { return getApiUrl('NEXT_PUBLIC_ACCESS_MANAGEMENT_URL', '/api/access-management'); },

  /** Secret Management service */
  get secretManagement() { return getApiUrl('NEXT_PUBLIC_SECRET_MANAGEMENT_URL', '/api/secrets'); },

  /** TCM - Trino Catalog Manager */
  get tcm() { return getApiUrl('NEXT_PUBLIC_TCM_URL', '/api/tcm'); },

  /** Observability MCP service */
  get observabilityMcp() { return getApiUrl('NEXT_PUBLIC_OBSERVABILITY_MCP_URL', '/api/observability'); },

  /** DSL Registry service */
  get registry() { return getApiUrl('NEXT_PUBLIC_REGISTRY_URL', '/api/registry'); },

  /** Webhook service */
  get webhook() { return getApiUrl('NEXT_PUBLIC_WEBHOOK_URL', '/api/webhook'); },
};

/**
 * Keycloak configuration (always requires full URL - separate domain)
 */
export const KeycloakConfig = {
  url: process.env.NEXT_PUBLIC_KEYCLOAK_URL || '',
  realm: process.env.NEXT_PUBLIC_KEYCLOAK_REALM || '',
  clientId: process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT_ID || '',
  disabled: process.env.NEXT_PUBLIC_KEYCLOAK_DISABLE === 'true',
} as const;

/**
 * Server-side only URLs (for API routes, not exposed to browser)
 * These are used by Next.js API routes to proxy requests
 */
export const ServerServices = {
  /** MCP server URLs (used in /api/mcp/* routes) */
  mcp: {
    tcm: process.env.TCM_MCP_URL || 'http://localhost:10004',
    metadata: process.env.METADATA_MCP_URL || 'http://localhost:10001/mcp',
  },
} as const;

/**
 * API version configuration
 */
export const ApiVersions = {
  accessManagement: process.env.NEXT_PUBLIC_ACCESS_MANAGEMENT_VERSION || '0.0.0',
  secretManagement: process.env.NEXT_PUBLIC_SECRET_MANAGEMENT_VERSION || '0.0.0',
  workflowManager: process.env.NEXT_PUBLIC_WORKFLOW_MANAGER_VERSION || '0.0.0',
} as const;

/**
 * Build full URL for a service with optional version
 */
export function getServiceUrl(service: keyof typeof Services, version?: string): string {
  const baseUrl = Services[service];
  if (version) {
    return `${baseUrl}/release/${version}`;
  }
  return baseUrl;
}

/**
 * Get Access Management (EAMS) full URL with version
 */
export function getAccessManagementUrl(): string {
  return getServiceUrl('accessManagement', ApiVersions.accessManagement);
}

/**
 * Get Secret Management full URL with version
 */
export function getSecretManagementUrl(): string {
  return getServiceUrl('secretManagement', ApiVersions.secretManagement);
}

/**
 * Get Workflow Manager full URL with version
 */
export function getWorkflowManagerUrl(): string {
  return getServiceUrl('workflowManager', ApiVersions.workflowManager);
}

/**
 * Runtime identity - MUST be set by the consuming application before using the library
 * Call setIdentity() to configure these values
 */
let _tenantId: string | null = null;
let _projectId: string | null = null;
let _userId: string | null = null;

/**
 * Set the identity for multi-tenancy. MUST be called before using the library.
 */
export function setIdentity(identity: { tenantId: string; projectId: string; userId: string }): void {
  _tenantId = identity.tenantId;
  _projectId = identity.projectId;
  _userId = identity.userId;
}

/**
 * Get the current identity. Throws if not set.
 */
export function getIdentity(): { tenantId: string; projectId: string; userId: string } {
  if (!_tenantId || !_projectId || !_userId) {
    throw new Error(
      'Identity not configured. Call setIdentity({ tenantId, projectId, userId }) before using the library.'
    );
  }
  return { tenantId: _tenantId, projectId: _projectId, userId: _userId };
}

/**
 * Check if identity has been set
 */
export function isIdentitySet(): boolean {
  return _tenantId !== null && _projectId !== null && _userId !== null;
}

// Export environment check for use in other modules
export { isDev };
